/**
 * AiPoweredFraudAlgorithmsPage — fraud algorithm management dashboard.
 * Algorithm list + per-algo enable toggle + threshold slider + recent flag count.
 * Recent flag count is driven off the backend fraud events list (verdict != clean).
 */
import { useMemo, useState } from "react";
import { useAiPoweredFraudAlgorithmsList } from "./hooks";

interface AlgorithmConfig {
	id: string;
	name: string;
	description: string;
	signals: string[];
	enabled: boolean;
	threshold: number;
}

const DEFAULT_ALGORITHMS: AlgorithmConfig[] = [
	{
		id: "velocity-guard",
		name: "Velocity Guard",
		description: "Detects abnormal click or install velocity per IP and device.",
		signals: ["ip_click_velocity", "click_to_install_too_fast"],
		enabled: true,
		threshold: 0.4,
	},
	{
		id: "network-shield",
		name: "Network Shield",
		description: "Flags datacenter, VPN, Tor and known-bad IP traffic.",
		signals: ["datacenter_ip", "vpn", "tor", "known_bad_ip"],
		enabled: true,
		threshold: 0.5,
	},
	{
		id: "device-replay",
		name: "Device Replay Scanner",
		description: "Catches replayed installs and missing install referrers.",
		signals: ["device_replay", "install_referrer_missing"],
		enabled: true,
		threshold: 0.4,
	},
	{
		id: "bot-heuristics",
		name: "Bot Heuristics",
		description: "User agent, JA3 fingerprint and proxy header mismatches.",
		signals: ["ua_bot_signature", "ja3_flagged", "proxy_headers_mismatch"],
		enabled: false,
		threshold: 0.3,
	},
	{
		id: "geo-consistency",
		name: "Geo Consistency",
		description: "Flags suspicious geo mismatches between click and install.",
		signals: ["geo_mismatch", "click_id_unsigned"],
		enabled: false,
		threshold: 0.3,
	},
];

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const ACCENT_TEXT = "text-green-700";
const ACCENT_BORDER = "border-green-600";
const ACCENT_BG = "bg-green-600";

function classNames(...parts: Array<string | false | null | undefined>): string {
	return parts.filter(Boolean).join(" ");
}

function isFlagged(verdict: unknown): boolean {
	return verdict === "suspicious" || verdict === "fraud";
}

export function AiPoweredFraudAlgorithmsPage() {
	const listQuery = useAiPoweredFraudAlgorithmsList();
	const [algorithms, setAlgorithms] = useState<AlgorithmConfig[]>(DEFAULT_ALGORITHMS);

	const items = listQuery.data?.items ?? [];
	const recentFlagTotal = useMemo(
		() =>
			items.reduce(
				(n, row) => (isFlagged((row as { verdict?: unknown }).verdict) ? n + 1 : n),
				0,
			),
		[items],
	);

	const flaggedBySignal = useMemo(() => {
		const map = new Map<string, number>();
		for (const row of items) {
			const verdict = (row as { verdict?: unknown }).verdict;
			if (!isFlagged(verdict)) continue;
			const raw = (row as { signals_json?: unknown }).signals_json;
			let parsed: unknown = [];
			if (typeof raw === "string") {
				try {
					parsed = JSON.parse(raw);
				} catch {
					parsed = [];
				}
			} else if (Array.isArray(raw)) {
				parsed = raw;
			}
			if (!Array.isArray(parsed)) continue;
			for (const s of parsed) {
				if (typeof s === "string" && s.length > 0) {
					map.set(s, (map.get(s) ?? 0) + 1);
				}
			}
		}
		return map;
	}, [items]);

	function toggle(id: string) {
		setAlgorithms((prev) =>
			prev.map((a) => (a.id === id ? { ...a, enabled: !a.enabled } : a)),
		);
	}

	function setThreshold(id: string, threshold: number) {
		setAlgorithms((prev) =>
			prev.map((a) => (a.id === id ? { ...a, threshold } : a)),
		);
	}

	const enabledCount = algorithms.filter((a) => a.enabled).length;

	return (
		<main
			data-testid="ai-powered-fraud-algorithms-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					ai-powered-fraud-algorithms
				</p>
				<h1 className={classNames("text-3xl font-semibold", ACCENT_TEXT)}>
					Fraud Algorithms
				</h1>
				<p className="text-sm text-zinc-600">
					Tune detection models, enable or disable individual algorithms, and
					monitor recent flagged events.
				</p>
			</header>

			<section
				className="grid grid-cols-1 gap-4 sm:grid-cols-3"
				aria-label="Fraud detection summary"
			>
				<div className={CARD} data-testid="ai-powered-fraud-algorithms-summary-enabled">
					<p className="text-xs uppercase tracking-wider text-zinc-500">
						Enabled algorithms
					</p>
					<p className={classNames("mt-2 text-2xl font-semibold", ACCENT_TEXT)}>
						{enabledCount} / {algorithms.length}
					</p>
				</div>
				<div className={CARD} data-testid="ai-powered-fraud-algorithms-summary-flags">
					<p className="text-xs uppercase tracking-wider text-zinc-500">
						Recent flagged events
					</p>
					<p className={classNames("mt-2 text-2xl font-semibold", ACCENT_TEXT)}>
						{listQuery.isLoading ? "..." : recentFlagTotal}
					</p>
				</div>
				<div className={CARD} data-testid="ai-powered-fraud-algorithms-summary-total">
					<p className="text-xs uppercase tracking-wider text-zinc-500">
						Events scored
					</p>
					<p className={classNames("mt-2 text-2xl font-semibold", ACCENT_TEXT)}>
						{listQuery.isLoading ? "..." : items.length}
					</p>
				</div>
			</section>

			<section className={CARD} aria-label="Algorithm list">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">Algorithms</h2>
					<span className="text-xs text-zinc-500">
						{algorithms.length} available
					</span>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="ai-powered-fraud-algorithms-loading"
					>
						Loading recent flag counts.
					</p>
				) : listQuery.error ? (
					<p className="mt-4 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : null}

				<ul
					className="mt-4 divide-y divide-zinc-200"
					data-testid="ai-powered-fraud-algorithms-list"
				>
					{algorithms.map((algo) => {
						const flagCount = algo.signals.reduce(
							(sum, sig) => sum + (flaggedBySignal.get(sig) ?? 0),
							0,
						);
						return (
							<li
								key={algo.id}
								className="flex flex-col gap-3 py-4"
								data-testid={`ai-powered-fraud-algorithms-row-${algo.id}`}
							>
								<div className="flex items-start justify-between gap-4">
									<div className="flex flex-col">
										<span className="text-sm font-semibold text-zinc-900">
											{algo.name}
										</span>
										<span className="mt-0.5 text-xs text-zinc-600">
											{algo.description}
										</span>
									</div>
									<div className="flex flex-col items-end gap-2">
										<label
											className="flex cursor-pointer items-center gap-2 text-xs font-medium text-zinc-700"
											data-testid={`ai-powered-fraud-algorithms-toggle-${algo.id}`}
										>
											<input
												type="checkbox"
												className="sr-only"
												checked={algo.enabled}
												onChange={() => toggle(algo.id)}
												data-testid={`ai-powered-fraud-algorithms-toggle-input-${algo.id}`}
											/>
											<span
												className={classNames(
													"relative inline-block h-5 w-9 rounded-full border transition-colors",
													algo.enabled
														? classNames(ACCENT_BG, ACCENT_BORDER)
														: "border-zinc-300 bg-zinc-200",
												)}
												aria-hidden="true"
											>
												<span
													className={classNames(
														"absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all",
														algo.enabled ? "left-4" : "left-0.5",
													)}
												/>
											</span>
											<span>{algo.enabled ? "Enabled" : "Disabled"}</span>
										</label>
										<span
											className={classNames(
												"rounded-[4px] border px-2 py-0.5 text-[11px] font-medium",
												flagCount > 0
													? classNames(
															ACCENT_BORDER,
															"bg-green-50",
															ACCENT_TEXT,
														)
													: "border-zinc-200 bg-zinc-50 text-zinc-600",
											)}
											data-testid={`ai-powered-fraud-algorithms-flag-count-${algo.id}`}
										>
											{flagCount} recent flag{flagCount === 1 ? "" : "s"}
										</span>
									</div>
								</div>

								<div className="flex flex-col gap-1">
									<div className="flex items-center justify-between text-xs text-zinc-600">
										<span>Threshold</span>
										<span
											className="font-mono"
											data-testid={`ai-powered-fraud-algorithms-threshold-value-${algo.id}`}
										>
											{algo.threshold.toFixed(2)}
										</span>
									</div>
									<input
										type="range"
										min={0}
										max={1}
										step={0.01}
										value={algo.threshold}
										onChange={(e) =>
											setThreshold(algo.id, Number(e.currentTarget.value))
										}
										disabled={!algo.enabled}
										className="h-2 w-full cursor-pointer appearance-none rounded-full bg-zinc-200 accent-green-600 disabled:cursor-not-allowed disabled:opacity-50"
										data-testid={`ai-powered-fraud-algorithms-threshold-${algo.id}`}
										aria-label={`${algo.name} threshold`}
									/>
								</div>

								<div className="flex flex-wrap gap-1.5">
									{algo.signals.map((sig) => (
										<span
											key={sig}
											className="rounded-[4px] border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 font-mono text-[11px] text-zinc-700"
										>
											{sig}
										</span>
									))}
								</div>
							</li>
						);
					})}
				</ul>

				{algorithms.length === 0 ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="ai-powered-fraud-algorithms-empty"
					>
						No algorithms configured.
					</p>
				) : null}
			</section>
		</main>
	);
}

export default AiPoweredFraudAlgorithmsPage;
